/**
 * BYO v2 — edit-source save() extracted from {@link useByoEditSource}.
 *
 * Owns the one operation in the edit path that actually writes to the
 * backend: building the vestige-free source payload, deciding whether the
 * attached code container should be updated in place or a fresh one
 * created, calling `requestUpdateSourceById`, and rolling back an orphaned
 * fresh container if that update fails. Pulled out on its own because it's
 * the riskiest single piece of `useByoEditSource` (the only part with
 * backend writes + rollback semantics) — see the code-review discussion on
 * NEX-21939 for why this needed isolating from the hydration/classification
 * logic that stayed behind in `useByoEditSource`.
 */

import { useCallback, useState, type MutableRefObject } from 'react';
import {
  createCodeContainer,
  updateCodeContainer,
  deleteCodeContainer,
} from 'js/actions/transform';
import { buildByoSourcePayload, sanitizeSourceConfigForSave } from '../persistence';
import {
  isRuntimeVarInvalid,
  serializeRuntimeVars,
  type BYOPerformanceConfig,
  type ByoCodeMode,
  type RuntimeVar,
} from 'new/components/flows/types/byo/core/model';
import type { ByoInputValue } from '../types';

export interface UseByoSourceSaveArgs {
  id?: number;
  input: ByoInputValue;
  code: string;
  codeMode: ByoCodeMode;
  predefinedId: number | string | null;
  packages: string[];
  runtimeVars: RuntimeVar[];
  performanceConfig: BYOPerformanceConfig;
  showPerfConfig: boolean;
  name: string;
  source: any;
  codeContainerId: number | string | null;
  /** Is the container CURRENTLY attached to the source one this hook owns (vs. a shared reusable one)? */
  attachedIsReusableRef: MutableRefObject<boolean>;
  dispatch: (action: any) => any;
  requestUpdateSourceById: (id: number, patch: any) => Promise<any>;
  /** Re-arm hydration state after a successful save (see useByoEditSource for why). */
  setCodeContainerId: (id: number | string | null) => void;
  /** Source/container hydration failed — the draft may hold default code, so never write it. */
  hydrationFailed: boolean;
  /**
   * extra_data already saved on the attached container. Used as the merge base
   * when that container is updated in place (see mergeExtraData, services.ts).
   */
  baseExtraData?: string;
}

export interface UseByoSourceSave {
  isSaving: boolean;
  saveError: string | null;
  save: (opts?: { markCodeConfigured?: boolean }) => Promise<any>;
}

type SaveGuardArgs = Pick<
  UseByoSourceSaveArgs,
  'hydrationFailed' | 'codeMode' | 'predefinedId' | 'runtimeVars'
>;

/** Why save() must not write right now, or null when it may. */
export function getSaveBlockedReason({
  hydrationFailed,
  codeMode,
  predefinedId,
  runtimeVars,
}: SaveGuardArgs): string | null {
  if (hydrationFailed) return 'The saved code could not be loaded. Reopen the editor to try again.';
  // NEX-22636: "Use Existing Function" with nothing picked used to fall
  // through to ensureCodeContainerId's create/update branch and PUT the
  // leftover own-code draft into the private container while marking the
  // source `reusable_code_container`. Guarded here (not only by the modal's
  // Save gating) so no caller can bypass it.
  if (codeMode === 'predefined' && predefinedId == null) {
    return 'Pick a function to use, or switch to "Provide Your Own Function".';
  }
  // Number('') is 0, so an empty/non-numeric Number variable would otherwise
  // be persisted as a value the user never gave (NEX-22636). Only own-code
  // saves persist runtime variables (a predefined pick is a direct reference —
  // its extra_data is never rewritten).
  if (codeMode === 'custom' && runtimeVars.some(isRuntimeVarInvalid)) {
    return 'Fix the invalid runtime variables before saving.';
  }
  return null;
}

/**
 * The container to update in place, or null to force a fresh private one.
 *
 * Update-in-place is only correct when the container CURRENTLY attached is one
 * we own (non-reusable). Updating a shared reusable container in 'custom' mode
 * would overwrite code other sources/orgs share and flip it to
 * `reusable: false` (confirmed live 2026-08-21). 'predefined' never reaches
 * this decision — ensureCodeContainerId's direct-reference short-circuit
 * ignores `existingContainerId`.
 */
export function containerIdToUpdate(
  codeMode: ByoCodeMode,
  attachedIsReusable: boolean,
  codeContainerId: number | string | null,
): number | string | null {
  return codeMode === 'custom' && attachedIsReusable ? null : codeContainerId;
}

/**
 * Did this save CREATE the container it's attaching? Only then may a failed
 * source update roll it back (otherwise it's orphaned). Never true when an
 * OWNED container was updated in place, nor for a predefined pick — that's a
 * direct reference to a shared function, and deleting it would destroy it
 * (PR #3212 follow-up, fix 1).
 */
export function isFreshContainer({
  existingContainerId,
  savedContainerId,
  codeMode,
  predefinedId,
}: {
  existingContainerId: number | string | null;
  savedContainerId: number | string | null | undefined;
  codeMode: ByoCodeMode;
  predefinedId: number | string | null;
}): boolean {
  return (
    existingContainerId == null &&
    savedContainerId != null &&
    !(codeMode === 'predefined' && predefinedId != null)
  );
}

/**
 * source_config with the current code-mode markers, so the tab restores
 * correctly on reopen and switching modes CLEARS the other mode's marker
 * (payload.source_config carries forward whatever a previous save left —
 * the original "reopens on the wrong tab" bug).
 */
export function withCodeModeMarkers(
  sourceConfig: Record<string, unknown>,
  {
    codeMode,
    predefinedId,
    markCodeConfigured,
  }: { codeMode: ByoCodeMode; predefinedId: number | string | null; markCodeConfigured: boolean },
): Record<string, unknown> {
  const next: Record<string, unknown> = {
    ...sourceConfig,
    ...(markCodeConfigured ? { 'ui.byo_code_configured': true } : {}),
    'ui.use_custom_processor': true,
    'ui.byo_code_predefined_id': codeMode === 'predefined' ? predefinedId : null,
    'ui.custom_processor_mode':
      codeMode === 'predefined' ? 'reusable_code_container' : 'custom_code',
  };
  return next;
}

export function useByoSourceSave(args: UseByoSourceSaveArgs): UseByoSourceSave {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const save = useCallback(
    async (opts?: { markCodeConfigured?: boolean }) => {
      const {
        id,
        input,
        code,
        codeMode,
        predefinedId,
        packages,
        runtimeVars,
        performanceConfig,
        showPerfConfig,
        name,
        source,
        codeContainerId,
        attachedIsReusableRef,
        dispatch,
        requestUpdateSourceById,
        setCodeContainerId,
        baseExtraData,
      } = args;

      const blockedReason = getSaveBlockedReason(args);
      // `!id` checked here (not in the helper) so TS narrows `id` for the PUT below.
      if (blockedReason || !id) {
        setSaveError(blockedReason ?? 'No source id to update.');
        return null;
      }
      setIsSaving(true);
      setSaveError(null);
      try {
        const existingContainerIdForSave = containerIdToUpdate(
          codeMode,
          attachedIsReusableRef.current,
          codeContainerId,
        );

        // Build a clean BYO source payload (vestige-free), reusing the v2
        // builder. `codeMode: 'predefined'` with a real `predefinedId` is
        // handled by `ensureCodeContainerId` (services.ts) by pointing
        // `code_container_id` straight at that container — no create, no
        // update, no copying its code anywhere. See that function's doc for
        // why a direct reference is safe (the picker only ever offers
        // resource_type: 'source_custom' ids).
        const payload = await buildByoSourcePayload(
          {
            input,
            code: {
              mode: codeMode,
              code,
              predefinedId,
              packages,
              extraData: serializeRuntimeVars(runtimeVars),
            },
            sourceConfigArgs: {
              sourceConfig: {
                ...sanitizeSourceConfigForSave(source?.source_config ?? {}),
                path: input.folderPath ?? '',
              },
              configFields: [],
              selectedConnection: input.connectionType,
            } as any,
            performanceConfig: showPerfConfig ? performanceConfig : undefined,
            name,
            // Update the source's existing (OWNED) container in place
            // instead of creating (and orphaning the old one) on every save
            // — confirmed bug, fixed 2026-08-19. `null` here (see above)
            // deliberately forces a fresh create instead, when the attached
            // container isn't ours to update.
            existingContainerId: existingContainerIdForSave,
            // Only applied by buildByoSourcePayload when updating that
            // existing container in place (never inherited by a fresh one).
            baseExtraData,
            // Only relevant for `codeMode: 'custom'` (predefined mode never
            // reaches a create/update payload at all — see the direct-
            // reference short-circuit in `ensureCodeContainerId`). Own code
            // the user is actively typing/editing is always private —
            // reusable containers are only made via an explicit "Save as
            // reusable" action elsewhere, never implicitly by this save.
            reusable: false,
          },
          {
            dispatch: dispatch as any,
            createCodeContainerAction: createCodeContainer as any,
            updateCodeContainerAction: updateCodeContainer as any,
          },
        );

        const augmentedSourceConfig = withCodeModeMarkers(payload.source_config, {
          codeMode,
          predefinedId,
          markCodeConfigured: !!opts?.markCodeConfigured,
        });
        const isNewContainer = isFreshContainer({
          existingContainerId: existingContainerIdForSave,
          savedContainerId: payload.code_container_id,
          codeMode,
          predefinedId,
        });
        let res: any;
        try {
          res = await requestUpdateSourceById(id, {
            name: payload.name,
            flow_type: payload.flow_type,
            code_container_id: payload.code_container_id,
            source_config: augmentedSourceConfig,
          });
        } catch (updateErr) {
          if (isNewContainer) {
            try {
              await dispatch(deleteCodeContainer(payload.code_container_id as any) as any);
            } catch {
              /* best-effort only — surface the original error either way */
            }
          }
          throw updateErr;
        }

        // Re-arm for a possible SECOND save in this same session (modal
        // stays open, user edits again without closing/reopening): without
        // this, a second custom-mode save would still see the stale
        // pre-save `codeContainerId`/`attachedIsReusableRef` and either
        // re-create yet another container, or — if the ref still said
        // "reusable" from before this save's fresh-create — miss updating
        // the container this save JUST created. `payload.code_container_id`
        // is exactly what's now attached; a custom-mode save always makes
        // it non-reusable (`reusable: false` above), a predefined-mode save
        // always references an already-reusable container.
        setCodeContainerId(payload.code_container_id);
        attachedIsReusableRef.current = codeMode === 'predefined';

        const updated =
          (res as any)?.entities?.sources?.[id] ?? (res as any)?.response ?? (res as any) ?? null;
        return updated;
      } catch (e: any) {
        setSaveError(e?.message ?? 'Failed to update the BYO source.');
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      args.id,
      args.input,
      args.code,
      args.codeMode,
      args.predefinedId,
      args.packages,
      args.runtimeVars,
      args.performanceConfig,
      args.showPerfConfig,
      args.name,
      args.source,
      args.codeContainerId,
      args.dispatch,
      args.requestUpdateSourceById,
      args.hydrationFailed,
      args.baseExtraData,
    ],
  );

  return { isSaving, saveError, save };
}

export default useByoSourceSave;
